// src/components/governance/LOCPortal.tsx
// DROP 26B — Local Organising Committee (LOC) Full Portal
// LOWER-LEVEL GOVERNANCE: Day-to-day ops, village supervision, tasks
// 22 Village LOCs + Special Communities — field operations management

import React, { useState, useEffect, useCallback } from 'react';
import {
  MapPin, Users, CheckCircle, Clock, AlertCircle, Plus,
  Phone, Mail, Loader2,
  Activity, BarChart2, Home,
  Target
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────

interface LOC {
  id: string; villageId: string; villageName: string;
  chairmanName: string; chairmanPhone: string; chairmanEmail?: string;
  secretaryName?: string; secretaryPhone?: string;
  memberCount: number; status: 'pending' | 'established' | 'active' | 'dissolved';
  notes?: string; establishedDate: string;
  tasks?: Task[];
}

interface Task {
  id: string; title: string; locId: string; villageName: string;
  assignee: string; status: 'pending' | 'in-progress' | 'completed';
  category: 'logistics' | 'registration' | 'publicity' | 'security' | 'hospitality' | 'general';
  dueDate?: string; priority: 'high' | 'medium' | 'low';
}

const C = {
  bg: '#070D0A', surface: '#0E1812', card: '#121E16',
  border: '#1C2E22', gold: '#C9A84C', goldLt: '#E2C97E',
  text: '#EDE8DC', muted: 'rgba(237,232,220,0.5)',
  dim: 'rgba(237,232,220,0.25)', green: '#0F5132', darkGreen: '#0A3D26',
};

const VILLAGE_ICONS: Record<string, string> = {
  'v01': '👑', 'v02': '🛡️', 'v03': '🌳', 'v04': '🌊',
  'v05': '🎭', 'v06': '⚔️', 'v07': '🌿', 'v08': '🌾',
  'v09': '🏺', 'v10': '🥊', 'v11': '⚖️', 'v12': '🧵',
  'v13': '🏛️', 'v14': '⚒️', 'v15': '📚', 'v16': '🎪',
  'v17': '🏪', 'v18': '🌿', 'v19': '🌲', 'v20': '🌴',
  'v21': '⛰️', 'v22': '💰', 'sp01': '🤝', 'sp02': '🌟',
  'sp03': '🎺', 'sp04': '✈️',
};

const ALL_VILLAGES = [
  { id: 'v01', name: 'Jattu-Uzairue' },    { id: 'v02', name: 'Afashio-Uzairue' },
  { id: 'v03', name: 'Afowa-Uzairue' },    { id: 'v04', name: 'Apana-Uzairue' },
  { id: 'v05', name: 'Ayaoghena-Uzairue' },{ id: 'v06', name: 'Ayogwiri-Uzairue' },
  { id: 'v07', name: 'Ayua-Uzairue' },     { id: 'v08', name: 'Elele-Uzairue' },
  { id: 'v09', name: 'Idatto-Uzairue' },   { id: 'v10', name: 'Ikabigbo-Uzairue' },
  { id: 'v11', name: 'Ikholo-Uzairue' },   { id: 'v12', name: 'Imeke-Uzairue' },
  { id: 'v13', name: 'Imhonikhe-Uzairue' },{ id: 'v14', name: 'Irekpai-Uzairue' },
  { id: 'v15', name: 'Iyamho-Uzairue' },   { id: 'v16', name: 'Iyora-Uzairue' },
  { id: 'v17', name: 'Iyuku-Uzairue' },    { id: 'v18', name: 'Ogbido-Uzairue' },
  { id: 'v19', name: 'Ozor-Uzairue' },     { id: 'v20', name: 'Ugbenor-Uzairue' },
  { id: 'v21', name: 'Uluoke-Uzairue' },   { id: 'v22', name: 'Yelwa-Uzairue' },
  { id: 'sp01', name: 'Igala Community' }, { id: 'sp02', name: 'Igbo Community' },
  { id: 'sp03', name: 'Yoruba Community' },{ id: 'sp04', name: 'Diaspora Community' },
];

const TASK_CATEGORIES = ['logistics', 'registration', 'publicity', 'security', 'hospitality', 'general'];
const CAT_COLORS: Record<string, string> = {
  logistics: '#38bdf8', registration: '#22c55e', publicity: C.gold,
  security: '#ef4444', hospitality: '#a78bfa', general: '#94a3b8',
};

function TabBtn({ active, onClick, children, icon }:
  { key?: React.Key; active: boolean; onClick: () => void; children: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 6,
      padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
      background: active ? C.green : 'transparent',
      color: active ? C.goldLt : C.muted, fontSize: 13,
      fontWeight: active ? 600 : 400, whiteSpace: 'nowrap' as const,
      borderBottom: active ? `2px solid ${C.gold}` : '2px solid transparent',
    }}>
      {icon && <span>{icon}</span>}{children}
    </button>
  );
}

function StatusBadge({ status }: { status: string }) {
  const m: Record<string, { bg: string; color: string; label: string }> = {
    active:      { bg: 'rgba(34,197,94,0.12)',  color: '#22c55e', label: '● Active' },
    established: { bg: 'rgba(34,197,94,0.12)',  color: '#22c55e', label: '✓ Established' },
    pending:     { bg: 'rgba(201,168,76,0.12)', color: '#C9A84C', label: '⏳ Pending' },
    dissolved:   { bg: 'rgba(239,68,68,0.12)',  color: '#ef4444', label: '✗ Dissolved' },
    'in-progress':{ bg: 'rgba(201,168,76,0.12)', color: '#C9A84C', label: '⟳ In Progress' },
    completed:   { bg: 'rgba(34,197,94,0.12)',  color: '#22c55e', label: '✓ Done' },
  };
  const s = m[status] || m.pending;
  return <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 10, fontWeight: 600,
    background: s.bg, color: s.color }}>{s.label}</span>;
}

interface Props { currentUser?: { email: string; systemRole?: string } | null; }

export default function LOCPortal({ currentUser }: Props) {
  const [tab, setTab] = useState<'overview' | 'locs' | 'tasks' | 'reports'>('overview');
  const [locs, setLocs] = useState<LOC[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [selectedLoc, setSelectedLoc] = useState<LOC | null>(null);
  const [showLocForm, setShowLocForm] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [locForm, setLocForm] = useState({
    villageId: 'v01', chairmanName: '', chairmanPhone: '',
    chairmanEmail: '', secretaryName: '', secretaryPhone: '', memberCount: 0, notes: '',
  });
  const [taskForm, setTaskForm] = useState({
    title: '', locId: '', villageName: '', assignee: '',
    category: 'general', priority: 'medium', dueDate: '',
  });

  const flash = (type: 'ok' | 'err', text: string) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4000); };

  const loadLocs = useCallback(async () => {
    try {
      const r = await fetch('/api/public/locs');
      if (r.ok) {
        const d = await r.json();
        setLocs(d.locs || []);
      }
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { loadLocs(); }, [loadLocs]);

  // DROP 35A — LOC Member management
  const [showMemberModal, setShowMemberModal] = React.useState(false);
  const [selectedLocId, setSelectedLocId] = React.useState<string>('');
  const [locMemberForm, setLocMemberForm] = React.useState({ name: '', role: 'Member', phone: '', email: '', village: '' });
  const [savingLocMember, setSavingLocMember] = React.useState(false);

  const handleAddLocMember = async () => {
    if (!locMemberForm.name.trim()) { flash('err', 'Member name required'); return; }
    setSavingLocMember(true);
    const token = localStorage.getItem('erue_auth_token') || sessionStorage.getItem('erue_auth_token') || '';
    try {
      const r = await fetch('/api/admin/locs/' + selectedLocId + '/members', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify(locMemberForm),
      });
      const d = await r.json();
      if (d.success) {
        setLocs(prev => prev.map(l => l.id === selectedLocId
          ? { ...l, memberCount: (l.memberCount || 0) + 1 }
          : l
        ));
        setLocMemberForm({ name: '', role: 'Member', phone: '', email: '', village: '' });
        setShowMemberModal(false);
        flash('ok', 'Member added to LOC successfully.');
      } else { flash('err', d.error || 'Failed to add member'); }
    } catch { flash('err', 'Network error'); }
    finally { setSavingLocMember(false); }
  };

  const handleAddLoc = async () => {
    if (!locForm.chairmanName.trim()) { flash('err', 'Chairman name required'); return; }
    const village = ALL_VILLAGES.find(v => v.id === locForm.villageId);
    const token = localStorage.getItem('erue_auth_token') || sessionStorage.getItem('erue_auth_token') || '';
    try {
      const r = await fetch('/api/admin/locs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...locForm, villageName: village?.name || locForm.villageId,
          status: 'active', establishedDate: new Date().toISOString(),
        }),
      });
      const d = await r.json();
      if (d.success) {
        setLocs(prev => [d.loc, ...prev]);
        setShowLocForm(false);
        setLocForm({ villageId: 'v01', chairmanName: '', chairmanPhone: '',
          chairmanEmail: '', secretaryName: '', secretaryPhone: '', memberCount: 0, notes: '' });
        flash('ok', `LOC for ${village?.name} established successfully.`);
      } else { flash('err', d.error || 'Failed to create LOC'); }
    } catch { flash('err', 'Network error'); }
  };

  const handleAddTask = () => {
    if (!taskForm.title.trim()) { flash('err', 'Task title required'); return; }
    const locInfo = locs.find(l => l.id === taskForm.locId);
    setTasks(prev => [{
      id: `t${Date.now()}`, ...taskForm as any,
      villageName: locInfo?.villageName || 'General',
      status: 'pending' as const,
    }, ...prev]);
    setShowTaskForm(false);
    setTaskForm({ title: '', locId: '', villageName: '', assignee: '',
      category: 'general', priority: 'medium', dueDate: '' });
    flash('ok', 'Task assigned.');
  };

  const updateTaskStatus = (id: string, status: Task['status']) => {
    setTasks(prev => prev.map(t => t.id === id ? { ...t, status } : t));
    flash('ok', 'Task status updated.');
  };

  const updateLocStatus = async (locId: string, status: LOC['status']) => {
    const token = localStorage.getItem('erue_auth_token') || sessionStorage.getItem('erue_auth_token') || '';
    try {
      await fetch(`/api/admin/locs/${locId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status }),
      });
      setLocs(prev => prev.map(l => l.id === locId ? { ...l, status } : l));
      flash('ok', 'LOC status updated.');
    } catch { flash('err', 'Update failed'); }
  };

  const userRole = (currentUser?.systemRole || '').toUpperCase();
  const isAdmin = ['PLATFORM_ADMIN', 'FESTIVAL_COORDINATOR'].includes(userRole) ||
                  currentUser?.email === 'erueuzairue@gmail.com';

  const activeLocs = locs.filter(l => l.status === 'active' || l.status === 'established').length;
  const pendingLocs = locs.filter(l => l.status === 'pending').length;
  const totalMembers = locs.reduce((s, l) => s + (l.memberCount || 0), 0);
  const missingVillages = ALL_VILLAGES.filter(v => !locs.find(l => l.villageId === v.id));
  const filteredLocs = filterStatus === 'all' ? locs : locs.filter(l => l.status === filterStatus);

  return (
    <div style={{ background: C.bg, minHeight: '100vh', padding: '24px 0' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 20px' }}>

        {/* Header */}
        <div style={{ background: `linear-gradient(135deg, #060F09 0%, #0A2015 100%)`,
          borderRadius: 16, padding: '28px 32px', marginBottom: 24,
          border: `1px solid rgba(201,168,76,0.2)`, position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', bottom: -30, left: -20, width: 100, height: 100,
            borderRadius: '50%', background: 'rgba(201,168,76,0.05)' }} />
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap' as const, gap: 16 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <div style={{ width: 36, height: 36, borderRadius: 8,
                  background: 'rgba(201,168,76,0.1)', border: `1px solid rgba(201,168,76,0.2)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Home size={18} style={{ color: C.gold }} />
                </div>
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2,
                  textTransform: 'uppercase' as const, color: C.gold }}>
                  Lower-Level Governance · Field Operations
                </span>
              </div>
              <h1 style={{ fontFamily: 'Cinzel, serif', fontSize: 24, fontWeight: 700,
                color: C.text, margin: 0, lineHeight: 1.2 }}>
                Local Organising Committees
              </h1>
              <p style={{ fontSize: 13, color: C.muted, margin: '6px 0 0' }}>
                22 villages and component communities · Day-to-day operations · Field task management
              </p>
            </div>
            <div style={{ textAlign: 'right' as const, fontSize: 11, color: C.dim }}>
              <div style={{ color: C.gold, fontWeight: 600, marginBottom: 4 }}>Uzairue Kingdom LOC Network</div>
              <div>{activeLocs} of {ALL_VILLAGES.length} communities active</div>
              <div>{missingVillages.length} awaiting establishment</div>
            </div>
          </div>
        </div>

        {/* Flash */}
        {msg && (
          <div style={{ padding: '10px 16px', borderRadius: 8, marginBottom: 16,
            background: msg.type === 'ok' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
            border: `1px solid ${msg.type === 'ok' ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
            color: msg.type === 'ok' ? '#22c55e' : '#ef4444', fontSize: 13,
            display: 'flex', alignItems: 'center', gap: 8 }}>
            {msg.type === 'ok' ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
            {msg.text}
          </div>
        )}

        {/* Metrics */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 24 }}>
          {[
            { icon: <MapPin size={16} />, label: 'Active LOCs', value: activeLocs, color: '#22c55e' },
            { icon: <Clock size={16} />, label: 'Pending LOCs', value: pendingLocs, color: C.gold },
            { icon: <AlertCircle size={16} />, label: 'Not Yet Set Up', value: missingVillages.length, color: '#ef4444' },
            { icon: <Users size={16} />, label: 'Total Members', value: totalMembers, color: '#38bdf8' },
            { icon: <CheckCircle size={16} />, label: 'Tasks Completed', value: tasks.filter(t => t.status === 'completed').length, color: '#22c55e' },
          ].map((m, i) => (
            <div key={i} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '14px 16px',
              display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 38, height: 38, borderRadius: 9,
                background: `${m.color}15`, border: `1px solid ${m.color}25`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ color: m.color }}>{m.icon}</span>
              </div>
              <div>
                <div style={{ fontSize: 20, fontWeight: 700, color: C.text, lineHeight: 1.2 }}>{m.value}</div>
                <div style={{ fontSize: 10, color: C.muted }}>{m.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${C.border}`, marginBottom: 24, overflowX: 'auto' as const }}>
          {([
            { key: 'overview', label: 'Overview', icon: <Target size={13} /> },
            { key: 'locs', label: 'Village LOCs', icon: <MapPin size={13} /> },
            { key: 'tasks', label: 'Task Board', icon: <Activity size={13} /> },
            { key: 'reports', label: 'Reports', icon: <BarChart2 size={13} /> },
          ] as const).map(t => (
            <TabBtn key={t.key} active={tab === t.key} onClick={() => setTab(t.key)} icon={t.icon}>
              {t.label}
            </TabBtn>
          ))}
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
            <Loader2 size={32} style={{ color: C.gold, animation: 'spin 1s linear infinite' }} />
          </div>
        ) : (
          <>
            {/* ── OVERVIEW ── */}
            {tab === 'overview' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {/* Mandate */}
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
                  <h3 style={{ fontFamily: 'Cinzel, serif', fontSize: 14, color: C.gold, marginBottom: 14 }}>
                    🏘️ LOC Mandate
                  </h3>
                  {[
                    { icon: '📋', title: 'Daily Task Execution', desc: 'Assign and supervise daily festival preparation tasks within each village' },
                    { icon: '👥', title: 'Community Mobilisation', desc: 'Organize village participation, registration drives and community attendance' },
                    { icon: '🔍', title: 'Quality Supervision', desc: 'Monitor service delivery, venue readiness and community standards during festival' },
                    { icon: '🛠️', title: 'Issue Resolution', desc: 'Handle ground-level problems, staff disputes and logistical challenges' },
                    { icon: '📊', title: 'Progress Reporting', desc: 'Report daily field progress to CWC for coordination and escalation' },
                  ].map((item, i) => (
                    <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 10, padding: '8px 12px',
                      background: `${C.bg}80`, borderRadius: 8, border: `1px solid ${C.border}` }}>
                      <span style={{ fontSize: 16 }}>{item.icon}</span>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{item.title}</div>
                        <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.4 }}>{item.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Village status grid */}
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
                  <h3 style={{ fontFamily: 'Cinzel, serif', fontSize: 14, color: C.gold, marginBottom: 12 }}>
                    🗺️ 26 Communities Status
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, maxHeight: 380, overflowY: 'auto' as const }}>
                    {ALL_VILLAGES.map(v => {
                      const loc = locs.find(l => l.villageId === v.id);
                      return (
                        <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 6,
                          padding: '6px 8px', borderRadius: 7, background: `${C.bg}80`,
                          border: `1px solid ${loc ? 'rgba(34,197,94,0.2)' : C.border}` }}>
                          <span style={{ fontSize: 13 }}>{VILLAGE_ICONS[v.id] || '🏘️'}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 10, fontWeight: 600, color: C.text,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
                              {v.name.replace('-Uzairue', '')}
                            </div>
                            {loc ? (
                              <div style={{ fontSize: 9, color: '#22c55e' }}>✓ {loc.status}</div>
                            ) : (
                              <div style={{ fontSize: 9, color: '#ef4444' }}>Not set up</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* ── VILLAGE LOCs ── */}
            {tab === 'locs' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' as const, gap: 10 }}>
                  <h2 style={{ fontFamily: 'Cinzel, serif', fontSize: 18, color: C.text, margin: 0 }}>
                    Village LOCs ({locs.length} established)
                  </h2>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                      style={{ padding: '7px 12px', background: C.card, border: `1px solid ${C.border}`,
                        borderRadius: 8, color: C.text, fontSize: 12, outline: 'none' }}>
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="established">Established</option>
                      <option value="pending">Pending</option>
                    </select>
                    {isAdmin && (
                    <button onClick={() => setShowLocForm(!showLocForm)}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
                        background: C.green, border: `1px solid rgba(201,168,76,0.3)`, borderRadius: 8,
                        color: C.gold, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                      <Plus size={14} /> Establish LOC
                    </button>
                    )}
                  </div>
                </div>

                {isAdmin && showLocForm && (
                  <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
                    <h3 style={{ color: C.gold, fontSize: 13, marginBottom: 14 }}>Establish New Village LOC</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ display: 'block', fontSize: 10, fontWeight: 700, letterSpacing: 1,
                          textTransform: 'uppercase' as const, color: C.muted, marginBottom: 6 }}>Community *</label>
                        <select value={locForm.villageId} onChange={e => setLocForm(prev => ({ ...prev, villageId: e.target.value }))}
                          style={{ width: '100%', padding: '9px 12px', background: C.bg,
                            border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13, outline: 'none' }}>
                          {ALL_VILLAGES.filter(v => !locs.find(l => l.villageId === v.id)).map(v => (
                            <option key={v.id} value={v.id}>{VILLAGE_ICONS[v.id]} {v.name}</option>
                          ))}
                        </select>
                      </div>
                      {[
                        { label: 'Chairman Name *', key: 'chairmanName', placeholder: 'e.g. Chief John Idah' },
                        { label: 'Chairman Phone *', key: 'chairmanPhone', placeholder: '+234...' },
                        { label: 'Chairman Email', key: 'chairmanEmail', placeholder: 'chairman@email.com' },
                        { label: 'Secretary Name', key: 'secretaryName', placeholder: 'e.g. Mrs. Ada Okafor' },
                        { label: 'Secretary Phone', key: 'secretaryPhone', placeholder: '+234...' },
                      ].map(field => (
                        <div key={field.key}>
                          <label style={{ display: 'block', fontSize: 10, fontWeight: 700, letterSpacing: 1,
                            textTransform: 'uppercase' as const, color: C.muted, marginBottom: 6 }}>{field.label}</label>
                          <input type="text" placeholder={field.placeholder}
                            value={(locForm as any)[field.key]}
                            onChange={e => setLocForm(prev => ({ ...prev, [field.key]: e.target.value }))}
                            style={{ width: '100%', padding: '9px 12px', background: C.bg,
                              border: `1px solid ${C.border}`, borderRadius: 8, color: C.text,
                              fontSize: 13, outline: 'none', boxSizing: 'border-box' as const }} />
                        </div>
                      ))}
                      <div>
                        <label style={{ display: 'block', fontSize: 10, fontWeight: 700, letterSpacing: 1,
                          textTransform: 'uppercase' as const, color: C.muted, marginBottom: 6 }}>Member Count</label>
                        <input type="number" min="0" placeholder="0"
                          value={locForm.memberCount}
                          onChange={e => setLocForm(prev => ({ ...prev, memberCount: parseInt(e.target.value) || 0 }))}
                          style={{ width: '100%', padding: '9px 12px', background: C.bg,
                            border: `1px solid ${C.border}`, borderRadius: 8, color: C.text,
                            fontSize: 13, outline: 'none', boxSizing: 'border-box' as const }} />
                      </div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ display: 'block', fontSize: 10, fontWeight: 700, letterSpacing: 1,
                          textTransform: 'uppercase' as const, color: C.muted, marginBottom: 6 }}>Notes</label>
                        <textarea rows={2} placeholder="Any additional notes..."
                          value={locForm.notes}
                          onChange={e => setLocForm(prev => ({ ...prev, notes: e.target.value }))}
                          style={{ width: '100%', padding: '9px 12px', background: C.bg,
                            border: `1px solid ${C.border}`, borderRadius: 8, color: C.text,
                            fontSize: 13, outline: 'none', resize: 'vertical' as const, boxSizing: 'border-box' as const }} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                      <button onClick={handleAddLoc}
                        style={{ padding: '8px 18px', background: C.gold, border: 'none', borderRadius: 8,
                          color: '#000', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Establish LOC</button>
                      <button onClick={() => setShowLocForm(false)}
                        style={{ padding: '8px 14px', background: 'none', border: `1px solid ${C.border}`,
                          borderRadius: 8, color: C.muted, fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                    </div>
                  </div>
                )}

                {/* Missing villages alert */}
                {missingVillages.length > 0 && (
                  <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
                    borderRadius: 10, padding: '10px 16px', marginBottom: 16,
                    display: 'flex', alignItems: 'center', gap: 8 }}>
                    <AlertCircle size={14} style={{ color: '#ef4444', flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: C.muted }}>
                      <strong style={{ color: '#ef4444' }}>{missingVillages.length} communities</strong> have not yet established their LOC.
                      Click "Establish LOC" to set them up.
                    </span>
                  </div>
                )}

                {filteredLocs.length === 0 ? (
                  <div style={{ textAlign: 'center' as const, padding: 60, color: C.dim }}>
                    <MapPin size={40} style={{ margin: '0 auto 12px', opacity: 0.3, display: 'block' }} />
                    <p>No LOCs established yet. Set up the first village LOC above.</p>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
                    {filteredLocs.map(loc => (
                      <div key={loc.id} style={{ background: C.card, border: `1px solid ${C.border}`,
                        borderRadius: 12, padding: 18 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ fontSize: 24 }}>{VILLAGE_ICONS[loc.villageId] || '🏘️'}</div>
                            <div>
                              <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{loc.villageName}</div>
                              <StatusBadge status={loc.status} />
                            </div>
                          </div>
                          <div style={{ fontSize: 11, color: C.muted, textAlign: 'right' as const }}>
                            <div style={{ color: C.text, fontWeight: 600 }}>{loc.memberCount}</div>
                            <div>members</div>
                          </div>
                        </div>

                        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10, marginTop: 4 }}>
                          <div style={{ fontSize: 11, color: C.gold, fontWeight: 600, marginBottom: 4 }}>Chairman</div>
                          <div style={{ fontSize: 13, color: C.text, marginBottom: 4 }}>{loc.chairmanName}</div>
                          {loc.chairmanPhone && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: C.muted, marginBottom: 2 }}>
                              <Phone size={10} /> {loc.chairmanPhone}
                            </div>
                          )}
                          {loc.chairmanEmail && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: C.muted, marginBottom: 2 }}>
                              <Mail size={10} /> {loc.chairmanEmail}
                            </div>
                          )}
                          {loc.secretaryName && (
                            <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.border}` }}>
                              <div style={{ fontSize: 11, color: C.dim, marginBottom: 2 }}>Secretary: <span style={{ color: C.muted }}>{loc.secretaryName}</span></div>
                              {loc.secretaryPhone && <div style={{ fontSize: 10, color: C.dim }}>{loc.secretaryPhone}</div>}
                            </div>
                          )}
                          {loc.notes && <p style={{ fontSize: 11, color: C.dim, marginTop: 8, lineHeight: 1.4 }}>{loc.notes}</p>}
                        </div>

                        {isAdmin && (
                        <div style={{ display: 'flex', gap: 6, marginTop: 12, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
                          {(['active', 'pending'] as const).map(s => (
                            <button key={s} onClick={() => updateLocStatus(loc.id, s)}
                              style={{ padding: '4px 10px', background: loc.status === s ? C.green : 'none',
                                border: `1px solid ${loc.status === s ? C.gold : C.border}`,
                                borderRadius: 5, color: loc.status === s ? C.gold : C.muted,
                                fontSize: 10, cursor: 'pointer', textTransform: 'capitalize' as const }}>
                              {s}
                            </button>
                          ))}
                          <button onClick={() => { setTaskForm(prev => ({ ...prev, locId: loc.id, villageName: loc.villageName })); setShowTaskForm(true); setTab('tasks'); }}
                            style={{ padding: '4px 10px', background: 'none',
                              border: `1px solid ${C.border}`, borderRadius: 5,
                              color: C.muted, fontSize: 10, cursor: 'pointer', marginLeft: 'auto' }}>
                            + Task
                          </button>
                          <button onClick={() => { setSelectedLocId(loc.id); setLocMemberForm({ name: '', role: 'Member', phone: '', email: '', village: loc.villageName }); setShowMemberModal(true); }}
                            style={{ padding: '4px 10px', background: 'none',
                              border: `1px solid rgba(201,168,76,0.4)`, borderRadius: 5,
                              color: C.gold, fontSize: 10, cursor: 'pointer' }}>
                            + Member
                          </button>
                        </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── TASK BOARD ── */}
            {tab === 'tasks' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h2 style={{ fontFamily: 'Cinzel, serif', fontSize: 18, color: C.text, margin: 0 }}>
                    Field Task Board ({tasks.length})
                  </h2>
                  {isAdmin && (
                  <button onClick={() => setShowTaskForm(!showTaskForm)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
                      background: C.green, border: `1px solid rgba(201,168,76,0.3)`, borderRadius: 8,
                      color: C.gold, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    <Plus size={14} /> Assign Task
                  </button>
                  )}
                </div>

                {isAdmin && showTaskForm && (
                  <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
                    <h3 style={{ color: C.gold, fontSize: 13, marginBottom: 14 }}>Assign Field Task</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ display: 'block', fontSize: 10, fontWeight: 700, letterSpacing: 1,
                          textTransform: 'uppercase' as const, color: C.muted, marginBottom: 6 }}>Task Title *</label>
                        <input type="text" placeholder="e.g. Set up registration desk at Jattu community hall"
                          value={taskForm.title}
                          onChange={e => setTaskForm(prev => ({ ...prev, title: e.target.value }))}
                          style={{ width: '100%', padding: '9px 12px', background: C.bg,
                            border: `1px solid ${C.border}`, borderRadius: 8, color: C.text,
                            fontSize: 13, outline: 'none', boxSizing: 'border-box' as const }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 10, fontWeight: 700, letterSpacing: 1,
                          textTransform: 'uppercase' as const, color: C.muted, marginBottom: 6 }}>Village LOC</label>
                        <select value={taskForm.locId} onChange={e => {
                          const loc = locs.find(l => l.id === e.target.value);
                          setTaskForm(prev => ({ ...prev, locId: e.target.value, villageName: loc?.villageName || '' }));
                        }}
                          style={{ width: '100%', padding: '9px 12px', background: C.bg,
                            border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13, outline: 'none' }}>
                          <option value="">General</option>
                          {locs.map(l => <option key={l.id} value={l.id}>{l.villageName}</option>)}
                        </select>
                      </div>
                      {[
                        { label: 'Assignee', key: 'assignee', placeholder: 'e.g. LOC Secretary' },
                        { label: 'Due Date', key: 'dueDate', placeholder: 'YYYY-MM-DD' },
                      ].map(field => (
                        <div key={field.key}>
                          <label style={{ display: 'block', fontSize: 10, fontWeight: 700, letterSpacing: 1,
                            textTransform: 'uppercase' as const, color: C.muted, marginBottom: 6 }}>{field.label}</label>
                          <input type="text" placeholder={field.placeholder}
                            value={(taskForm as any)[field.key]}
                            onChange={e => setTaskForm(prev => ({ ...prev, [field.key]: e.target.value }))}
                            style={{ width: '100%', padding: '9px 12px', background: C.bg,
                              border: `1px solid ${C.border}`, borderRadius: 8, color: C.text,
                              fontSize: 13, outline: 'none', boxSizing: 'border-box' as const }} />
                        </div>
                      ))}
                      <div>
                        <label style={{ display: 'block', fontSize: 10, fontWeight: 700, letterSpacing: 1,
                          textTransform: 'uppercase' as const, color: C.muted, marginBottom: 6 }}>Category</label>
                        <select value={taskForm.category} onChange={e => setTaskForm(prev => ({ ...prev, category: e.target.value as any }))}
                          style={{ width: '100%', padding: '9px 12px', background: C.bg,
                            border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13, outline: 'none' }}>
                          {TASK_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 10, fontWeight: 700, letterSpacing: 1,
                          textTransform: 'uppercase' as const, color: C.muted, marginBottom: 6 }}>Priority</label>
                        <select value={taskForm.priority} onChange={e => setTaskForm(prev => ({ ...prev, priority: e.target.value as any }))}
                          style={{ width: '100%', padding: '9px 12px', background: C.bg,
                            border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13, outline: 'none' }}>
                          {['high', 'medium', 'low'].map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                      <button onClick={handleAddTask}
                        style={{ padding: '8px 18px', background: C.gold, border: 'none', borderRadius: 8,
                          color: '#000', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Assign Task</button>
                      <button onClick={() => setShowTaskForm(false)}
                        style={{ padding: '8px 14px', background: 'none', border: `1px solid ${C.border}`,
                          borderRadius: 8, color: C.muted, fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                    </div>
                  </div>
                )}

                {/* Kanban columns */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
                  {(['pending', 'in-progress', 'completed'] as const).map(status => (
                    <div key={status} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
                      <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: C.text, textTransform: 'capitalize' as const }}>
                          {status === 'in-progress' ? '⟳ In Progress' : status === 'completed' ? '✓ Completed' : '○ Pending'}
                        </span>
                        <span style={{ fontSize: 11, color: C.muted }}>
                          {tasks.filter(t => t.status === status).length}
                        </span>
                      </div>
                      <div style={{ padding: 10, display: 'flex', flexDirection: 'column' as const, gap: 8, minHeight: 200 }}>
                        {tasks.filter(t => t.status === status).map(task => (
                          <div key={task.id} style={{ background: C.bg, border: `1px solid ${C.border}`,
                            borderLeft: `3px solid ${CAT_COLORS[task.category] || C.gold}`,
                            borderRadius: 8, padding: '10px 12px' }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 4, lineHeight: 1.3 }}>
                              {task.title}
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 4, marginBottom: 6 }}>
                              <span style={{ fontSize: 9, fontWeight: 600, padding: '2px 6px', borderRadius: 10,
                                background: `${CAT_COLORS[task.category]}20`, color: CAT_COLORS[task.category] }}>
                                {task.category}
                              </span>
                              <span style={{ fontSize: 9, color: task.priority === 'high' ? '#ef4444' : task.priority === 'medium' ? C.gold : '#22c55e' }}>
                                {task.priority === 'high' ? '🔴' : task.priority === 'medium' ? '🟡' : '🟢'} {task.priority}
                              </span>
                            </div>
                            {task.villageName && <div style={{ fontSize: 10, color: C.dim }}>🏘️ {task.villageName}</div>}
                            {task.assignee && <div style={{ fontSize: 10, color: C.dim }}>👤 {task.assignee}</div>}
                            {task.dueDate && <div style={{ fontSize: 10, color: C.dim }}>📅 {task.dueDate}</div>}
                            <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
                              {status !== 'in-progress' && status !== 'completed' && (
                                <button onClick={() => updateTaskStatus(task.id, 'in-progress')}
                                  style={{ flex: 1, padding: '3px 0', background: 'rgba(201,168,76,0.1)',
                                    border: '1px solid rgba(201,168,76,0.2)', borderRadius: 5,
                                    color: C.gold, fontSize: 9, cursor: 'pointer' }}>Start</button>
                              )}
                              {status !== 'completed' && (
                                <button onClick={() => updateTaskStatus(task.id, 'completed')}
                                  style={{ flex: 1, padding: '3px 0', background: 'rgba(34,197,94,0.1)',
                                    border: '1px solid rgba(34,197,94,0.2)', borderRadius: 5,
                                    color: '#22c55e', fontSize: 9, cursor: 'pointer' }}>Done</button>
                              )}
                            </div>
                          </div>
                        ))}
                        {tasks.filter(t => t.status === status).length === 0 && (
                          <div style={{ textAlign: 'center' as const, padding: 20, color: C.dim, fontSize: 11 }}>
                            No tasks
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── REPORTS ── */}
            {tab === 'reports' && (
              <div>
                <h2 style={{ fontFamily: 'Cinzel, serif', fontSize: 18, color: C.text, marginBottom: 20 }}>
                  LOC Network Reports
                </h2>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  {/* Coverage report */}
                  <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
                    <h3 style={{ fontSize: 14, color: C.gold, fontFamily: 'Cinzel, serif', marginBottom: 14 }}>
                      🗺️ Coverage Report
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 10 }}>
                      {[
                        { label: 'Total Communities', value: ALL_VILLAGES.length, color: C.text },
                        { label: 'LOCs Established', value: locs.length, color: '#22c55e' },
                        { label: 'Active LOCs', value: activeLocs, color: '#22c55e' },
                        { label: 'Pending LOCs', value: pendingLocs, color: C.gold },
                        { label: 'Not Yet Setup', value: missingVillages.length, color: '#ef4444' },
                        { label: 'Total Members', value: totalMembers, color: '#38bdf8' },
                        { label: 'Coverage %', value: `${Math.round((locs.length / ALL_VILLAGES.length) * 100)}%`, color: C.gold },
                      ].map((item, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '6px 0', borderBottom: i < 6 ? `1px solid ${C.border}` : 'none' }}>
                          <span style={{ fontSize: 12, color: C.muted }}>{item.label}</span>
                          <span style={{ fontSize: 14, fontWeight: 700, color: item.color }}>{item.value}</span>
                        </div>
                      ))}
                    </div>
                    {/* Coverage bar */}
                    <div style={{ marginTop: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: C.dim, marginBottom: 4 }}>
                        <span>LOC Network Coverage</span>
                        <span>{Math.round((locs.length / ALL_VILLAGES.length) * 100)}%</span>
                      </div>
                      <div style={{ height: 8, background: C.border, borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ height: '100%',
                          width: `${Math.round((locs.length / ALL_VILLAGES.length) * 100)}%`,
                          background: `linear-gradient(90deg, ${C.green}, ${C.gold})`, borderRadius: 4 }} />
                      </div>
                    </div>
                  </div>

                  {/* Task report */}
                  <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
                    <h3 style={{ fontSize: 14, color: C.gold, fontFamily: 'Cinzel, serif', marginBottom: 14 }}>
                      📋 Task Report
                    </h3>
                    {tasks.length === 0 ? (
                      <p style={{ fontSize: 12, color: C.dim, textAlign: 'center' as const, padding: 20 }}>
                        No tasks assigned yet. Use the Task Board to assign field tasks.
                      </p>
                    ) : (
                      <>
                        {TASK_CATEGORIES.map(cat => {
                          const catTasks = tasks.filter(t => t.category === cat);
                          if (catTasks.length === 0) return null;
                          return (
                            <div key={cat} style={{ marginBottom: 12 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                <span style={{ fontSize: 11, color: CAT_COLORS[cat], fontWeight: 600, textTransform: 'capitalize' as const }}>{cat}</span>
                                <span style={{ fontSize: 11, color: C.muted }}>{catTasks.filter(t => t.status === 'completed').length}/{catTasks.length} done</span>
                              </div>
                              <div style={{ height: 5, background: C.border, borderRadius: 3, overflow: 'hidden' }}>
                                <div style={{ height: '100%',
                                  width: catTasks.length > 0 ? `${(catTasks.filter(t => t.status === 'completed').length / catTasks.length) * 100}%` : '0%',
                                  background: CAT_COLORS[cat], borderRadius: 3 }} />
                              </div>
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>

                  {/* Missing villages */}
                  {missingVillages.length > 0 && (
                    <div style={{ gridColumn: '1 / -1', background: C.card, border: `1px solid rgba(239,68,68,0.2)`,
                      borderRadius: 12, padding: 20 }}>
                      <h3 style={{ fontSize: 14, color: '#ef4444', fontFamily: 'Cinzel, serif', marginBottom: 12 }}>
                        ⚠️ Communities Without LOC ({missingVillages.length})
                      </h3>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
                        {missingVillages.map(v => (
                          <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                            background: `${C.bg}80`, borderRadius: 8, border: `1px solid rgba(239,68,68,0.15)` }}>
                            <span style={{ fontSize: 16 }}>{VILLAGE_ICONS[v.id] || '🏘️'}</span>
                            <span style={{ fontSize: 12, color: C.text }}>{v.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {/* LOC Member Add Modal */}
      {isAdmin && showMemberModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#0E1812', border: '1px solid #1C2E22', borderRadius: 16,
            padding: 28, width: '100%', maxWidth: 480 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontFamily: 'Cinzel, serif', fontSize: 16, color: '#C9A84C', margin: 0 }}>
                Add LOC Member
              </h3>
              <button onClick={() => setShowMemberModal(false)}
                style={{ background: 'none', border: 'none', color: 'rgba(237,232,220,0.5)',
                  fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              {[
                { label: 'Full Name *', key: 'name', placeholder: 'Enter full name' },
                { label: 'Role', key: 'role', placeholder: 'e.g. Treasurer' },
                { label: 'Phone', key: 'phone', placeholder: '+234...' },
                { label: 'Email', key: 'email', placeholder: 'email@domain.com' },
              ].map((f) => (
                <div key={f.key}>
                  <label style={{ display: 'block', fontSize: 10, fontWeight: 700, letterSpacing: 1,
                    textTransform: 'uppercase' as const, color: 'rgba(237,232,220,0.5)', marginBottom: 4 }}>{f.label}</label>
                  <input
                    value={(locMemberForm as any)[f.key]}
                    onChange={e => setLocMemberForm(p => ({ ...p, [f.key]: e.target.value }))}
                    placeholder={f.placeholder}
                    style={{ width: '100%', background: '#070D0A', border: '1px solid #1C2E22',
                      borderRadius: 6, padding: '8px 12px', color: '#EDE8DC', fontSize: 13,
                      outline: 'none', boxSizing: 'border-box' as const }} />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleAddLocMember} disabled={savingLocMember}
                style={{ flex: 1, padding: '10px 20px', background: '#0F5132',
                  border: '1px solid rgba(201,168,76,0.4)', borderRadius: 8,
                  color: '#C9A84C', fontSize: 13, fontWeight: 700,
                  cursor: savingLocMember ? 'wait' : 'pointer',
                  opacity: savingLocMember ? 0.6 : 1 }}>
                {savingLocMember ? 'Saving...' : 'Add Member'}
              </button>
              <button onClick={() => setShowMemberModal(false)}
                style={{ padding: '10px 20px', background: 'transparent',
                  border: '1px solid #1C2E22', borderRadius: 8,
                  color: 'rgba(237,232,220,0.5)', fontSize: 13, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
